import { useState, useCallback, useMemo } from 'react'
import {
  DataGrid,
  type DataGridProps,
  gridClasses,
  type GridCellParams,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowClassNameParams,
} from '@mui/x-data-grid'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { cloneDeep } from 'lodash'
import { backgroundColorDepth, type TreeDataGridRow } from './utils'
import { ExpandableCell, iconMargin, iconWidth } from './ExpandableCell'
import { useLayout } from 'hooks/useLayout'
import { IndentedCell } from './IndentedCell'

const StyledDataGrid = styled(DataGrid<TreeDataGridRow>)(() => ({
  '& .MuiDataGrid-cell': {
    color: '#2C2C27',
  },
  '& .MuiDataGrid-columnHeaderCheckbox': {
    padding: 0,
  },
  ...backgroundColorDepth.reduce(
    (accumulator, current, index) => ({
      ...accumulator,
      [`& .${gridClasses.row}.depth-${index}`]: { backgroundColor: current },
    }),
    {}
  ),
}))

const defaultCellRenderer = (params: GridRenderCellParams, col: GridColDef) =>
  col.renderCell ? (
    col.renderCell(params)
  ) : (
    <Typography>{params.row[col.field]}</Typography>
  )

const createFirstCellRenderer =
  (col: GridColDef, onToggleExpand: (row: GridCellParams['row']) => void) =>
  // eslint-disable-next-line react/display-name
  (params: GridRenderCellParams) =>
    !!params.row.children && params.row.children.length > 0 ? (
      <IndentedCell depth={params.row.parentIndexPath.length}>
        <ExpandableCell
          expanded={!!params.row.expanded}
          toggleExpandHandler={() => onToggleExpand(params.row)}
          rowId={params.row.rowId}
        >
          {defaultCellRenderer(params, col)}
        </ExpandableCell>
      </IndentedCell>
    ) : (
      <IndentedCell
        depth={params.row.parentIndexPath.length}
        extraIndentation={iconWidth + iconMargin}
      >
        {defaultCellRenderer(params, col)}
      </IndentedCell>
    )

const getColumns = (
  columns: Array<GridColDef<TreeDataGridRow>>,
  onToggleExpand: (row: GridCellParams<TreeDataGridRow>['row']) => void
): GridColDef[] => {
  // this will never be used for mobile since there's no need to expand
  // rows on mobile
  return columns.map((col, idx) => {
    const commonConfig = {
      ...col,
      sortable: false,
    }

    if (idx === 0) {
      return {
        ...commonConfig,
        renderCell: createFirstCellRenderer(col, onToggleExpand),
      }
    } else {
      return commonConfig
    }
  })
}

const getRowsRecursively = (
  rows: TreeDataGridRow[],
  parentIndexPath: number[] = [],
  expandAll: boolean = false
): DataGridProps<TreeDataGridRow>['rows'] => {
  const allRows: TreeDataGridRow[] = []
  rows.forEach((row, index) => {
    const currentRow = row
    const rowWithIndexPath = {
      ...currentRow,
      parentIndexPath,
      index,
    }

    const childRows =
      (currentRow?.expanded === true || expandAll) &&
      currentRow.children &&
      currentRow.children.length > 0
        ? getRowsRecursively(
            currentRow.children,
            [...parentIndexPath, index],
            expandAll
          )
        : []

    allRows.push(rowWithIndexPath)
    childRows.forEach((childRow) => allRows.push(childRow))
  })
  return allRows
}

const toggleRowExpand = (row: TreeDataGridRow) => {
  const newRow: TreeDataGridRow = { ...row }
  newRow.expanded = !newRow.expanded
  return newRow
}

const findParentRow = (
  rows: TreeDataGridRow[] | undefined,
  parentIndexPath: number[],
  row?: TreeDataGridRow
): TreeDataGridRow | undefined => {
  if (parentIndexPath.length > 0 && !!rows) {
    return findParentRow(
      rows[parentIndexPath[0]].children,
      parentIndexPath.slice(1),
      rows[parentIndexPath[0]]
    )
  } else {
    return row
  }
}

const getRowsExpandHandler = (
  rows: TreeDataGridRow[],
  row: GridCellParams<TreeDataGridRow>['row']
): TreeDataGridRow[] => {
  if (row.children && row.children.length > 0) {
    const newRows = cloneDeep(rows)
    if (row.parentIndexPath.length > 0) {
      const parentRow = findParentRow(newRows, row.parentIndexPath)
      if (parentRow?.children && parentRow?.children.length > 0) {
        parentRow.children[row.index] = toggleRowExpand(
          parentRow.children[row.index]
        )
      }
    } else {
      newRows[row.index] = toggleRowExpand(newRows[row.index])
    }
    return newRows
  }
  return rows
}

const getRowClassName = (dataGridParams: GridRowClassNameParams) =>
  `depth-${dataGridParams.row.parentIndexPath.length as string}`

interface TreeDataGridProps
  extends Omit<DataGridProps<TreeDataGridRow>, 'rows'> {
  rows: TreeDataGridRow[]
  desktopHeight?: string
  mobileColumns?: Array<GridColDef<TreeDataGridRow>>
  recalculateRows?: boolean
}

// It would be nice if this component could be mobile-agnostic
// (that is, mobile-specific behavior should be handled in the parent
// component). But that would require some major refactoring of how
// we handle the expandable cells.
export const TreeDataGrid = ({
  rows,
  columns,
  mobileColumns,
  desktopHeight = '100%',
  recalculateRows = false,
  ...dataGridProps
}: TreeDataGridProps) => {
  const { isMobile } = useLayout()
  // caveat: rows is only initialized once, so if the prop changes then it won't
  // necessarily be reflected in this component. so you should not render this component
  // until you have loaded all the data necessary to calculate rows.
  const [treeRows, setTreeRows] = useState<TreeDataGridRow[]>(rows)

  const onToggleExpand = useCallback(
    (row: GridCellParams['row']) => {
      setTreeRows(getRowsExpandHandler(treeRows, row))
    },
    [treeRows, setTreeRows]
  )

  const staticRows = getRowsRecursively(rows, [], isMobile)

  const recursiveRows = useMemo(
    () => getRowsRecursively(treeRows, [], isMobile),
    [treeRows, isMobile]
  )

  if (isMobile && mobileColumns) {
    return (
      <StyledDataGrid
        disableRowSelectionOnClick
        disableColumnMenu
        rows={recalculateRows ? staticRows : recursiveRows}
        columns={mobileColumns}
        getRowId={(row) => row.rowId}
        hideFooter
        getRowHeight={() => 'auto'}
        autoHeight
        getRowClassName={getRowClassName}
        {...dataGridProps}
      />
    )
  }

  const mutableColumns = [...columns]

  return (
    <StyledDataGrid
      disableRowSelectionOnClick
      disableColumnMenu
      rows={recalculateRows ? staticRows : recursiveRows}
      columns={getColumns(mutableColumns, onToggleExpand)}
      getRowClassName={getRowClassName}
      getRowId={(row) => row.rowId}
      hideFooter
      rowHeight={64}
      sx={{ overflowY: 'auto', height: desktopHeight, width: '100%' }}
      {...dataGridProps}
    />
  )
}
